// Week 2 / Day 3 — Nimny's School Shield: Bullying and Boundaries.
// Reworked: bilingual RU/EN inline; calm, empowering tone.
// 50% online + 50% paper/parent. Five layers preserved.
// SAFETY: behaviour-based; age-appropriate boundary wording; no graphic content.

const day3 = {
  id: 'day-3',
  studentId: 'max',
  weekId: 'week-2',
  dayId: 'day-3',
  status: 'active',
  title: 'Школьный щит Нимни: буллинг и границы / Nimny\'s School Shield: Bullying and Boundaries',
  shortTitle: 'Школьный щит / School Shield',
  theme:
    'В школе важно понимать разницу между конфликтом, шуткой, буллингом и нарушением границ. / At school it is important to understand the difference between conflict, joke, bullying and boundary crossing.',
  estimatedTimeLabel:
    'Онлайн-миссия: около 20–30 минут. Бумажная миссия с родителем: около 25–35 минут. / Online mission: about 20–30 minutes. Paper mission with parent: about 25–35 minutes.',
  nimnyIntro:
    'Нимни получила третью часть Щита безопасности — школьный щит. Он помогает понять, когда можно решить спокойно, а когда нужно уйти и рассказать взрослому. / Nimny received the third part of the Safety Shield — the school shield. It helps us know when we can solve something calmly and when we need to walk away and tell an adult.',
  goals: [
    'Слой 1. Математика: считать школьные ситуации, дроби и шаги помощи. / Layer 1. Math: count school situations, fractions and help steps.',
    'Слой 2. Русский язык и правописание: словарь границ, мини-диктант, исправление с «не». / Layer 2. Russian language and spelling: boundary vocabulary, mini-dictation, correction with "не".',
    'Слой 3. Чтение, письмо и пересказ: «Что делать, если в школе неприятная ситуация?» — план, пересказ и 3–5 предложений. / Layer 3. Reading, writing and retelling: "What to do if something uncomfortable happens at school?" — plan, retell and 3–5 sentences.',
    'Слой 4. Наблюдение и логика — конфликт, буллинг или нужна помощь. / Layer 4. Observation and Logic — Conflict, Bullying or Need Help.',
    'Слой 5. Практика на бумаге: «Школьный щит Нимни» с фразами «Stop / I need an adult» и сортировкой ситуаций. / Layer 5. Paper practice: "Nimny\'s School Shield" with phrases and situation sorting.',
    'Дневник мышления: одна спокойная фраза для школы. / Thinking diary: one calm school phrase.',
    'Награда нимами: базово 3 нима, максимум 5 с бонусами. / Reward in nims: 3 base, up to 5 with bonuses.'
  ],
  vocabulary: [
    { term: 'буллинг / bullying', definition: 'когда обижают повторно или давят сильнее. / when someone hurts repeatedly or uses power.' },
    { term: 'конфликт / conflict', definition: 'короткий спор, который можно решить спокойно. / a short disagreement that can be solved calmly.' },
    { term: 'граница / boundary', definition: 'моё «нельзя» — про моё тело, вещи и слова. / my "no" — about my body, things and words.' },
    { term: 'уважение / respect', definition: 'когда учитывают чужие границы. / when someone considers others\' boundaries.' },
    { term: 'обидно / hurtful', definition: 'когда слова или действия делают плохо. / when words or actions hurt.' },
    { term: 'стоп / stop', definition: 'короткое слово, которое говорит: «хватит». / a short word that says "enough".' },
    { term: 'уйти / walk away', definition: 'отойти, чтобы быть в безопасности. / move away to stay safe.' },
    { term: 'взрослый / adult', definition: 'безопасный взрослый, к которому я обращаюсь. / a safe adult I turn to.' },
    { term: 'доказательство / evidence', definition: 'фото, скриншот или запись, которую видит родитель. / a photo, screenshot or note shown to a parent.' },
    { term: 'сообщение / message', definition: 'если оно неприятное — показываю взрослому. / if it is hurtful, I show an adult.' }
  ],
  onlineTasks: [
    {
      id: 'q1',
      type: 'math',
      title: 'Отчёты школьного щита / School shield reports',
      prompt:
        'Что сделать / What to do: найди число отчётов для обсуждения со взрослым.\nВ школьном щите 96 отчётов. 1/4 — обычные конфликты, 3/8 — ситуации буллинга, 1/6 — ситуации с личными границами. Остальные отчёты нужно обсудить со взрослым. Сколько отчётов нужно обсудить со взрослым?\nThe school shield has 96 reports. 1/4 are ordinary conflicts, 3/8 are bullying situations, and 1/6 are boundary situations. The rest need to be discussed with an adult. How many reports need adult discussion?',
      helper: 'Найди три дробные части от 96, потом вычти их сумму из общего. / Find three fractional parts of 96, then subtract their sum from the total.',
      answer: '20',
      unit: 'отчётов / reports'
    },
    {
      id: 'q2',
      type: 'math',
      title: 'Карточки школьного щита / School shield cards',
      prompt:
        'Что сделать / What to do: найди число карточек, которым нужна помощь взрослого.\n5 групп учеников разобрали по 12 карточек. 15% всех карточек требуют помощи взрослого. Сколько карточек требуют помощи взрослого?\nFive groups reviewed 12 cards each. 15% of all cards need adult help. How many cards need adult help?',
      helper: 'Сначала найди общее количество карточек, потом процент от общего числа. / First find the total number of cards, then the percent of the total.',
      answer: '9',
      unit: 'карточек / cards'
    },
    {
      id: 'q3',
      type: 'math',
      title: 'План границ / Boundary plan',
      prompt:
        'Что сделать / What to do: найди, сколько пунктов в одном наборе.\nВ плане Нимни есть 2 одинаковых набора фраз, 3 одинаковых набора действий и ещё 10 карточек. Один набор фраз и один набор действий имеют одинаковое количество пунктов. Всего 85 пунктов. Сколько пунктов в одном наборе?\nNimny\'s plan has 2 equal phrase sets, 3 equal action sets, and 10 extra cards. One phrase set and one action set have the same number of items. In total, there are 85 items. How many items are in one set?',
      helper: 'Это уравнение: всего одинаковых наборов 2 + 3 = 5; убери дополнительные карточки, потом раздели остаток. / Equation: 5 equal sets total; subtract the extras and divide.',
      answer: '15',
      unit: 'пунктов в наборе / items per set'
    },
    {
      id: 'q4',
      type: 'math',
      title: 'Среднее число обращений / Average number of reports',
      prompt:
        'Что сделать / What to do: найди среднее (без округления).\nЗа 5 дней школьный щит получил 6, 8, 8, 11 и 13 обращений. Какое среднее число обращений за день? Ответ округлять не нужно.\nOver 5 days, the school shield received 6, 8, 8, 11, and 13 reports. What is the average number of reports per day? Do not round.',
      helper: 'Среднее = сумма всех значений ÷ количество значений. / Mean = sum of all values ÷ count of values.',
      answer: '9.2',
      unit: 'обращений в день / reports per day'
    },
    {
      id: 'q5',
      type: 'classify',
      title: '«Не» с глаголами и слова границ / "Не" with verbs and boundary words',
      prompt:
        'Как пишется «не»? И какая буква в корне? / How is "не" written? Which root letter?',
      categories: [
        { id: 'separate', label: '«не» раздельно с глаголом / "не" + verb separately' },
        { id: 'a', label: 'безударная «а» / unstressed "а"' },
        { id: 'o', label: 'безударная «о» / unstressed "о"' }
      ],
      items: [
        { id: 'ne_drat', text: '(не)толкать в ответ / "(не)толкать"' },
        { id: 'ne_molchat', text: '(не)молчать о буллинге / "(не)молчать"' },
        { id: 'ne_skryvat', text: '(не)скрывать страшный секрет / "(не)скрывать"' },
        { id: 'shkola', text: 'шк_ла — проверка: школьный / "shkola" — check: школьный' },
        { id: 'gran_ica', text: 'гр_ница — проверка: грань / "granitsa" — check: грань' },
        { id: 'soobshch', text: 'с_общение — проверка: общий / "soobshchenie" — check: общий' }
      ]
    },
    {
      id: 'q6',
      type: 'text',
      title: 'Мини-диктант / Mini-dictation',
      prompt:
        'Запиши без ошибок: «Если меня обижают, я могу сказать стоп, уйти и рассказать взрослому. Толкать в ответ небезопасно.» / Write without mistakes: "If someone hurts me, I can say stop, walk away and tell an adult. Pushing back is not safe."',
      placeholder:
        'Если меня обижают ... / If someone hurts me ...'
    },
    {
      id: 'q_corr',
      type: 'text',
      title: 'Исправь ошибки / Correct the mistakes',
      prompt:
        'Перепиши без ошибок: «еслi меня абижают я магу сказат стоп, уйти и расказать взрослому. Талкать в ответ небезопасно.» Подсказки: безударные гласные проверяй ударением; «сказать», «рассказать» — мягкий знак и удвоенная «с»; запятая после придаточного. / Rewrite the sentence; fix unstressed vowels, doubled «с» in «рассказать», soft sign in «сказать», comma after the dependent clause.',
      placeholder:
        'Если меня обижают, я могу сказать стоп, уйти и рассказать взрослому. Толкать в ответ небезопасно. / If someone hurts me, ...'
    },
    {
      id: 'q7',
      type: 'text',
      title: 'План текста и письмо / Text plan and writing',
      prompt:
        'Прочитай короткий текст: «Конфликт может случиться один раз и решиться спокойно. Буллинг — это когда обижают снова и снова или давят сильнее. Если мне неприятно, я могу сказать «стоп», уйти и рассказать безопасному взрослому. Рассказать — это сильное действие, не слабость.» Запиши план из 3 пунктов и 3–5 предложений на русском по теме: «Что я могу сделать, если меня обижают или мне неприятно?» / Read the short text: "A conflict can happen once and be solved calmly. Bullying is when someone hurts you again and again or presses harder. If something is hurtful, I can say «stop», walk away and tell a safe adult. Telling is strong, not weak." Write a 3-point plan and 3–5 Russian sentences on: "What can I do if someone hurts me or I feel hurt?"',
      placeholder:
        '1. ... 2. ... 3. ... Мои предложения: ... / 1. ... 2. ... 3. ... My sentences: ...'
    },
    {
      id: 'q_obs',
      type: 'text',
      title: 'Наблюдение и логика — конфликт, буллинг или нужна помощь / Observation and Logic — Conflict, Bullying or Need Help',
      prompt:
        'Реши коротко по 3 признакам:\n1) Это случилось один раз или повторяется? ____\n2) Мне обидно или больно? ____\n3) Есть давление или сила? ____\nМогу ли я безопасно сказать «стоп»? Нужно ли сохранить/показать сообщение? Сделай вывод одной фразой: «Это конфликт / буллинг / нарушение границы — поэтому я ___». / Decide by 3 signs: once or repeated? hurtful? pressure? Can I safely say «stop»? Save/show the message? One-line conclusion.',
      placeholder:
        'Раз / повтор: ... Обидно: ... Давление: ... Стоп: ... Сообщение: ... Вывод: ... / Once or repeat: ... Hurtful: ... Pressure: ... Stop: ... Message: ... Conclusion: ...'
    },
    {
      id: 'q8',
      type: 'classify',
      title: 'Школьные ситуации / School situations',
      prompt:
        'Распредели карточки по 5 корзинам. / Sort the cards into 5 baskets.',
      categories: [
        { id: 'conflict', label: 'Конфликт / Conflict' },
        { id: 'bully', label: 'Буллинг / Bullying' },
        { id: 'boundary', label: 'Нарушение границы / Boundary crossing' },
        { id: 'adult', label: 'Нужен взрослый / Need an adult' },
        { id: 'unclear', label: 'Непонятно — спросить взрослого / Unclear — ask an adult' }
      ],
      items: [
        { id: 'gameargue', text: 'два друга спорят, в какую игру играть / two friends argue about a game' },
        { id: 'dailytease', text: 'одного ребёнка каждый день дразнят / a child is teased every day' },
        { id: 'pushlaugh', text: 'кто-то толкает и смеётся / someone pushes and laughs' },
        { id: 'meanmsg', text: 'кто-то пишет неприятные сообщения / someone sends hurtful messages' },
        { id: 'scarysecret', text: 'кто-то просит хранить страшный секрет / someone asks to keep a scary secret' },
        { id: 'dontellparents', text: 'кто-то говорит: «не рассказывай родителям» / someone says "do not tell your parents"' },
        { id: 'sayingstop', text: 'ребёнок говорит: «стоп, мне неприятно» / a child says "stop, this hurts"' },
        { id: 'totheteacher', text: 'ребёнок идёт к учителю / a child goes to a teacher' }
      ]
    },
    {
      id: 'q9',
      type: 'essay',
      title: 'Дневник мышления: моя спокойная фраза / Thinking diary: my calm phrase',
      prompt:
        'Напиши 3–5 предложений: какую спокойную фразу я скажу в школе и кому потом расскажу. На английском добавь одну фразу: "Stop. I don\'t like that." или "I need an adult". Используй слова: стоп, уйти, взрослый. / Write 3–5 sentences: what calm phrase I will say at school and to whom I will tell. Add one English phrase. Use words: stop, walk away, adult.',
      minSentences: 3,
      maxSentences: 5,
      placeholder:
        'Моя спокойная фраза ... / My calm phrase ...'
    }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 2, День 3 — Школьный щит Нимни». / Heading: "Week 2, Day 3 — Nimny\'s School Shield".',
    'Слой 1. Математика: реши q1–q4 и нарисуй полосу 4 групп карточек (6/5/3/2). / Layer 1. Math: solve q1–q4 and draw a 4-bar chart.',
    'Слой 2. Русский язык: запиши словарь границ и мини-диктант. / Layer 2. Russian: write the boundary vocabulary and the mini-dictation.',
    'Слой 3. Чтение и пересказ: 3-пунктовый план + 3–5 предложений; перескажи родителю. / Layer 3. Reading and retelling: 3-point plan + 3–5 sentences; retell to a parent.',
    'Слой 4. Наблюдение и логика: запиши 3 признака, по которым отличаю буллинг от конфликта. / Layer 4. Observation and Logic: write 3 signs that distinguish bullying from conflict.',
    'Слой 5. Практика: запиши 3 фразы (стоп / ухожу / нужен взрослый) и одну фразу на английском. / Layer 5. Practice: write 3 phrases and one English phrase.',
    'Дневник мышления: одна спокойная фраза для школы. / Thinking diary: one calm school phrase.',
    'Награда нимами начисляется после проверки родителем. / Reward in nims is added after a parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 2, День 3 / Week 2, Day 3',
    subtitle: 'Школьный щит Нимни / Nimny\'s School Shield',
    pages: [
      {
        title: 'Маршрут миссии — Школьный щит Нимни / Mission Route — Nimny\'s School Shield',
        blocks: [
          { type: 'text', text: 'Сегодня — пять слоёв и одна миссия. Отмечай ☐ → ☑. / Today — five layers and one mission. Mark ☐ → ☑.' },
          { type: 'list', items: [
            '☐ Слой 1. Математика — около 10 минут. / Layer 1. Math — about 10 minutes.',
            '☐ Слой 2. Русский язык — около 10 минут. / Layer 2. Russian — about 10 minutes.',
            '☐ Слой 3. Чтение и пересказ — около 10 минут. / Layer 3. Reading and retelling — about 10 minutes.',
            '☐ Слой 4. Наблюдение и логика — около 5 минут. / Layer 4. Observation and Logic — about 5 minutes.',
            '☐ Слой 5. Практика на бумаге — около 15 минут. / Layer 5. Paper practice — about 15 minutes.',
            '☐ Дневник мышления — 5 минут. / Thinking diary — 5 minutes.',
            '☐ Печать миссии и награда нимами. / Mission stamp and nim reward.'
          ]}
        ]
      },
      {
        title: 'Слой 1. Математика — школьный щит в числах / Layer 1. Math — School Shield in Numbers',
        blocks: [
          { type: 'text', text: 'Математика уровня 5–6 класса. Запиши решение по шагам и финальный ответ. / Grade 5–6 math. Show steps and the final answer.' },
          { type: 'list', items: [
            '1) 96 отчётов: 1/4 конфликты, 3/8 буллинг, 1/6 границы, остальные — со взрослым. Сколько со взрослым? Ответ: ____ / 96 reports: 1/4, 3/8, 1/6; rest — adult discussion. Answer: ____',
            '   Шаги / Steps: __________________________________________',
            '2) 5 групп × 12 карточек, 15% требуют помощи взрослого. Сколько таких карточек? Ответ: ____ / 5 × 12 = ?; 15% of that = ?',
            '   Шаги / Steps: __________________________________________',
            '3) План: 2 одинаковых набора фраз + 3 одинаковых набора действий + 10 карточек = 85. Сколько в одном наборе? Ответ: ____ / 2x + 3x + 10 = 85',
            '   Шаги / Steps: __________________________________________',
            '4) За 5 дней обращений: 6, 8, 8, 11, 13. Среднее (без округления): ____ / Mean of 6, 8, 8, 11, 13',
            '   Шаги / Steps: __________________________________________'
          ]}
        ]
      },
      {
        title: 'Слой 2. Русский язык и правописание — слова границ / Layer 2. Russian Language and Spelling — Boundary Words',
        blocks: [
          { type: 'text', text: 'Правило дня: «не» с глаголами раздельно. / Rule of the day: "не" + verb separately.' },
          { type: 'list', items: [
            'Словарь миссии: буллинг, конфликт, граница, уважение, обидно, стоп, уйти, взрослый, доказательство, сообщение. / Mission vocabulary.',
            'Мини-диктант: «Если меня обижают, я могу сказать стоп, уйти и рассказать взрослому. Толкать в ответ небезопасно.» / Mini-dictation.',
            'Запиши без ошибок: __________________________________________ / Write without mistakes:',
            'Исправление: «еслi меня абижают я магу сказат стоп, уйти и расказать взрослому. Талкать в ответ небезопасно.» / Correct and rewrite the sentence with mistakes.',
            'Подчеркни «не» + глагол отдельно: не толкать, не молчать, не скрывать. / Underline "не" + verb (separately).'
          ]}
        ]
      },
      {
        title: 'Слой 3. Чтение, письмо и пересказ — неприятная ситуация в школе / Layer 3. Reading, Writing and Retelling',
        blocks: [
          { type: 'text', text: 'Прочитай короткий текст и сделай план. / Read the short text and make a plan.' },
          { type: 'list', items: [
            'Текст: «Конфликт может случиться один раз и решиться спокойно. Буллинг — это когда обижают снова и снова или давят сильнее. Если мне неприятно, я могу сказать «стоп», уйти и рассказать безопасному взрослому. Рассказать — это сильное действие, не слабость.» / Text.',
            'План: 1) ____________________  2) ____________________  3) ____________________ / Plan',
            'Главная мысль: ______________________________________ / Main idea',
            'Мои 3–5 предложений «Что я могу сделать, если меня обижают?»: / My 3–5 sentences',
            '1. ______________________________________________',
            '2. ______________________________________________',
            '3. ______________________________________________',
            '☐ Я пересказал родителю / I retold a parent.'
          ]}
        ]
      },
      {
        title: 'Слой 4. Наблюдение и логика — конфликт, буллинг или нужна помощь / Layer 4. Observation and Logic — Conflict, Bullying or Need Help',
        blocks: [
          { type: 'text', text: 'Понаблюдай: смотри на признаки, а не на внешность. / Observe: look at signs, not appearance.' },
          { type: 'list', items: [
            'Это случилось один раз или повторяется? ____________________ / Once or repeated?',
            'Мне обидно или больно? ____________________ / Hurtful?',
            'Есть давление или сила? ____________________ / Pressure or power?',
            'Могу ли я безопасно сказать «стоп»? ____________________ / Can I safely say "stop"?',
            'Нужно сохранить или показать сообщение? ____________________ / Save/show a message?',
            'Мой вывод (1 фраза): ____________________ / My conclusion (1 line):'
          ]}
        ]
      },
      {
        title: 'Слой 5. Практика на бумаге — школьный щит и фразы / Layer 5. Paper Practice — School Shield and Phrases',
        blocks: [
          { type: 'text', text: 'Нарисуй щит и запиши фразы. / Draw the shield and write the phrases.' },
          { type: 'list', items: [
            '[Нарисуй здесь школьный щит Нимни] / [Draw the Nimny School Shield here]',
            'Сортировка карточек по 5 корзинам: «Конфликт», «Буллинг», «Нарушение границы», «Нужен взрослый», «Непонятно». / Sort cards into 5 baskets.',
            '☐ Обведи 1 «конфликт». / Circle 1 conflict.',
            '☐ Обведи 1 «буллинг» красным. / Circle 1 bullying in red.',
            '☐ Обведи 1 «нужен взрослый». / Circle 1 need-an-adult.',
            'Мои фразы (потренируйся вслух): / My phrases (practise aloud):',
            '☐ «Стоп. Мне это не нравится.» / "Stop. I don\'t like that."',
            '☐ «Я ухожу.» / "I\'m walking away."',
            '☐ «Мне нужен взрослый.» / "I need an adult."',
            '☐ «Я расскажу родителю.» / "I will tell my parent."',
            '☐ «Помогите, пожалуйста.» / "Please help me."',
            'Мои границы (моё «нельзя»): / My boundaries:',
            '☐ Моё тело принадлежит мне. / My body belongs to me.',
            '☐ Я могу сказать «нет». / I can say no.',
            '☐ Если мне страшно или странно — я рассказываю безопасному взрослому. / If something feels scary or strange — I tell a safe adult.',
            '☐ Страшный секрет от родителей — это сигнал тревоги. / A scary secret from parents is a warning.',
            '[Поставь печать миссии] / [Place mission stamp]'
          ]}
        ]
      },
      {
        title: 'Дневник мышления / Thinking Diary',
        blocks: [
          { type: 'list', items: [
            'Моя спокойная фраза для школы: ______________________ / My calm school phrase',
            'Я выбрал её, потому что _________________________ / I chose it because',
            'Слово миссии, которое запомнилось: ________________ / Mission word I remember'
          ]}
        ]
      },
      {
        title: 'Родительская проверка и награда нимами / Parent Check and Nim Reward',
        blocks: [
          { type: 'text', text: 'После проверки начисли бонусные нимы через Parent Room. / After checking, add bonus nims through Parent Room.' },
          { type: 'list', items: [
            'Базовая награда: 3 нима ☐ / Base reward: 3 nims ☐',
            'Бонус за правильный школьный щит: +1 ☐ / School-shield bonus: +1 ☐',
            'Бонус за спокойную фразу помощи: +1 ☐ / Calm-help-phrase bonus: +1 ☐',
            'Итого: ____ из 5 нимов. / Total ____ of 5 nims.',
            'Печать миссии (родитель): ☐ / Mission stamp (parent): ☐',
            'Подпись родителя: ____________________ / Parent signature'
          ]}
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Ребёнок учится отличать конфликт, буллинг и нарушение границ; тренирует короткие спокойные фразы и понимает, что рассказать взрослому — это сила, а не слабость. / Child learns to tell apart conflict, bullying and boundary issues; practises short calm phrases; understands that telling an adult is strong, not weak.',
    mathGoal:
      'Уровень 5–6 класса: несколько дробей от числа (1/4, 3/8, 1/6 от 96); процент от произведения (15% × 60); линейное уравнение 5x + 10 = 85; среднее арифметическое (46 ÷ 5). / Grade 5–6: multiple fractions of a number; percent of a product; linear equation; arithmetic mean.',
    russianGoal:
      'Раздельное «не» с глаголами; правильно записанный мини-диктант; исправлены 3–5 предложений. / "не" + verb separately; correct mini-dictation; sentences corrected.',
    readingWritingGoal:
      'План из 3 пунктов; пересказ родителю; 3–5 предложений на русском. / 3-point plan; retell; 3–5 Russian sentences.',
    observationLogicGoal:
      'Ребёнок называет 3 признака (повторяется, обидно, есть давление) и понимает, что они важнее внешности. / Child names 3 signs and knows they matter more than appearance.',
    practicalGoal:
      'Карточки разложены по 5 корзинам; записаны 5 фраз (3 русские + 2 английские допустимо); проиграны 1–2 короткие сценки. / Cards into 5 baskets; 5 phrases written; 1–2 short role-plays done.',
    check: [
      'Q1: 20 (96 − (24+36+16)). Q2: 9 (15% × 60). Q3: 15 (5x+10=85). Q4: 9.2 (46÷5). / Math answers.',
      'Мини-диктант записан без грубых ошибок. / Mini-dictation OK.',
      'План текста — 3 пункта; пересказ — 3–5 предложений по-русски. / Plan = 3; retell 3–5 sentences.',
      'Карточки разложены по 5 корзинам логично. / Cards sorted into 5 baskets.',
      'Записаны фразы помощи; одна на английском проговорена вслух. / Phrases written; 1 EN phrase spoken.',
      'Дневник мышления заполнен. / Thinking diary filled.'
    ],
    answerKey: [
      { taskId: 'q1', answer: 'Answer: 20. Solution: 1/4 × 96 = 24; 3/8 × 96 = 36; 1/6 × 96 = 16; 24 + 36 + 16 = 76; 96 − 76 = 20. Parent note: несколько дробей от целого, потом остаток. / Grade 5–6: multiple fractions of a number + remainder.' },
      { taskId: 'q2', answer: 'Answer: 9. Solution: 5 × 12 = 60; 15% × 60 = 0.15 × 60 = 9. Parent note: сначала произведение, потом процент от полученного числа. / Grade 5–6: percent of a product.' },
      { taskId: 'q3', answer: 'Answer: 15. Solution: 2x + 3x + 10 = 85; 5x + 10 = 85; 5x = 75; x = 15. Parent note: один набор фраз и один набор действий имеют одинаковое количество пунктов — обозначаем как x; собираем подобные. / Grade 5–6: linear equation with combined like terms.' },
      { taskId: 'q4', answer: 'Answer: 9.2 обращения в день. Solution: 6 + 8 + 8 + 11 + 13 = 46; 46 ÷ 5 = 9.2. Parent note: среднее = сумма ÷ количество значений; ответ оставляем десятичным. / Grade 5–6: arithmetic mean, no rounding.' },
      { taskId: 'q5', answer: 'Answer: «не» раздельно — не толкать, не молчать, не скрывать. Безударные: школа — о (школьный); граница — а (грань); сообщение — о (общий). Solution: «не» с глаголами почти всегда раздельно; безударные — проверяем родственным словом с ударением. Parent note: если ребёнок не уверен — пусть проговорит проверку вслух. / "не" separate; vowels by check word.' },
      { taskId: 'q6', answer: 'Answer: «Если меня обижают, я могу сказать стоп, уйти и рассказать взрослому. Толкать в ответ небезопасно.» Solution: запятая после «обижают»; точки в конце; «небезопасно» — слитно с «не». Parent note: ≤ 2 ошибок — нормально. / Mini-dictation: ≤ 2 errors acceptable.' },
      { taskId: 'q_corr', answer: 'Answer: «Если меня обижают, я могу сказать стоп, уйти и рассказать взрослому. Толкать в ответ небезопасно.» Solution: еслi→Если (заглавная + и); абижают→обижают (проверка: обида); магу→могу (проверка: можешь); сказат→сказать (мягкий знак); расказать→рассказать (удвоенная «с»); Талкать→Толкать (проверка: толк/толчок). Parent note: 6 ошибок — для возраста засчитывай ≥ 5/6 исправлений; разбирай по одному. / Acceptance: ≥ 5/6.' },
      { taskId: 'q7', answer: 'Answer: 3-пунктовый план + 3–5 предложений. Solution: пример плана — 1) конфликт = разово, можно решить спокойно; 2) буллинг = повтор/давление; 3) стоп → уйти → рассказать взрослому. Parent note: ключевая мысль — рассказать это сила, не слабость. / 3-point plan; "telling is strength".' },
      { taskId: 'q_obs', answer: 'Answer: 1) повторяется (не один раз) → ближе к буллингу; 2) обидно → серьёзная ситуация; 3) давление/сила → красный сигнал. Solution: «стоп» безопасно сказать, если рядом не угроза физического насилия; если давление сильное — отойди и расскажи взрослому. Сообщения сохраняй/показывай родителю как доказательство. Вывод: например, «Это буллинг — поэтому я говорю «стоп», ухожу и рассказываю взрослому». Parent note: принимай вывод, который связывает 3 признака с действием. / 3-sign reasoning → action.' },
      { taskId: 'q8', answer: 'Answer: Конфликт — спор о игре. Буллинг — ежедневное дразнение, толчки со смехом, неприятные сообщения. Нарушение границы — страшный секрет; «не рассказывай родителям». Нужен взрослый — страшный секрет (тоже), ребёнок идёт к учителю. Непонятно — спросить взрослого: при сомнениях. «Стоп, мне неприятно» — пример безопасной реакции (не сортируется как ситуация). Solution: критерий — повторение и давление переводят ситуацию из «конфликт» в «буллинг»; «секрет» автоматически = нарушение границы. Parent note: спорные карточки обсуди вслух. / Sortings with reasoning.' },
      { taskId: 'q9', answer: 'Answer: открытый ответ, 3–5 предложений. Solution: пример — «Моя спокойная фраза: «Стоп, мне неприятно. Я ухожу.» На английском: "Stop. I don\'t like that." Расскажу классному учителю или маме.» Parent note: критерии — короткая русская фраза, имя взрослого, одна английская фраза. / Open answer with required parts.' }
    ],
    spellingCheck:
      'Раздельное «не» в 3/3; верные проверочные хотя бы для 2 из 3 безударных. / "не" 3/3; vowels ≥ 2/3.',
    readingCheck:
      'План — 3 пункта; пересказ по-русски без чтения с листа. / 3-point plan; retell in Russian without reading.',
    practicalCheck:
      'Карточки разложены логично; фразы записаны и хотя бы одна проговорена; границы названы простыми словами. / Cards sorted; phrases written; one spoken; boundaries in simple words.',
    rolePlayCheck:
      'Родитель проигрывает 1–2 сценки по 30–60 секунд. Ребёнок отвечает короткой фразой («Стоп, мне неприятно» / «I need an adult») и говорит, кому расскажет. / Parent role-plays 1–2 short scenes; child responds with a short phrase and names whom to tell.',
    discussionPrompts: [
      'Чем отличается конфликт от буллинга? / What is the difference between conflict and bullying?',
      'Почему рассказать взрослому — это сила, а не слабость? / Why is telling an adult strong, not weak?',
      'Что делать, если первый взрослый не услышал? / What if the first adult does not hear?',
      'Почему «секрет от родителей» — это сигнал тревоги? / Why is "secret from parents" a warning?'
    ],
    ifEasy:
      'Усложни: попроси придумать 3 школьные ситуации и расставить их по типам с обоснованием. / Add 3 school situations and label them with reasoning.',
    ifDifficult:
      'Возьми только 3 простые карточки (спор о игре, дразнение, страшный секрет) и пройдитесь вместе. Достаточно одной фразы. / Use 3 simple cards and one phrase. No pressure.',
    commonMistakes: [
      'Ребёнок думает, что рассказать взрослому — это «ябедничество». Уточни: безопасность важнее тишины. / Child thinks telling = tattling — clarify safety first.',
      'Ребёнок думает, что «дать сдачи» — лучшее решение. Уточни: сдача может ухудшить ситуацию; стоп / уйти / рассказать. / Child thinks fighting back is best — clarify the safer steps.'
    ],
    parentNote:
      'Тон спокойный, без страха. Хвалите за желание рассказывать, а не за молчание. / Calm tone, no fear. Praise telling, not silence.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'school-shield', label: 'Бонус за правильный школьный щит', amount: 1 },
      { id: 'calm-help-phrase', label: 'Бонус за спокойную фразу помощи', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: ['q1', 'q5', 'q7', 'q8', 'q9'],
    allowNotebookCompletion: true
  },
  nextLessonId: 'max-week-2-day-4'
};

export default day3;
